import React from 'react';
import { Autocomplete, TextField, InputAdornment, SxProps, Theme } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useLSContext } from '../../Context/useLSContext';
import { useDataContext } from '../../Context/useDataContext';
import { toIdString } from '../../utils/courseDataHelpers';

interface EventSelectorProps {
    availableEvents: any[];
    eventData: any;
    variant?: 'standard' | 'outlined' | 'filled';
    color?: string;
    sx?: SxProps<Theme>;
    showStartAdornment?: boolean;
    adornmentOffset?: number;
}

export const EventSelector: React.FC<EventSelectorProps> = ({ 
    availableEvents, 
    eventData, 
    variant = 'standard',
    color = 'primary.main',
    sx,
    showStartAdornment = true,
    adornmentOffset = 60,
}) => {
    const { factor, setLeaderboardScoreFormat, setStandingTabs } = useLSContext();
    const { setEventData } = useDataContext();
    const [searchParams, setSearchParams] = useSearchParams();

    const defaultSx: SxProps<Theme> = { 
        flex: 1,
        width: '98%',
        padding: '20px 30px 10px 30px',
        '& .MuiAutocomplete-popupIndicator': {
            display: 'none',
        },
        '& .MuiAutocomplete-clearIndicator .MuiSvgIcon-root': {
            color: color,
        },
    };

    return (
        <Autocomplete
            options={availableEvents || []}
            getOptionLabel={(option: any) => option?.eventName ?? ''}
            isOptionEqualToValue={(option: any, value: any) =>
                toIdString(option?._id) === toIdString(value?._id)
            }
            disablePortal
            value={eventData?._id ? eventData : null}
            renderInput={(params: any) => (
                <TextField
                    variant={variant}
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        ...(showStartAdornment && {
                            startAdornment: (
                                <InputAdornment position="start" sx={{ width: `${adornmentOffset}px`, margin: 0 }}>
                                </InputAdornment>
                            ),
                        }),
                    }}
                    inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password',
                    }}
                    placeholder="Select Event"
                    sx={{ 
                        width: '100%',
                        padding: 0,
                        fontSize: 12*factor*2,
                        color: color,
                        '& .MuiInputBase-input': {
                            color: color,
                            textAlign: 'center',
                        },
                        '& .MuiInput-underline:before': {
                            borderBottomColor: color,
                        },
                        '& .MuiInput-underline:hover:before': {
                            borderBottomColor: color,
                        },
                        '& .MuiInput-underline:after': {
                            borderBottomColor: color,
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: color,
                            opacity: 1,
                        },
                        ...(variant === 'outlined' && {
                            '& .MuiOutlinedInput-root': {
                                padding: '2px 4px',
                            },
                            '& .MuiOutlinedInput-input': {
                                padding: '4px 8px',
                            },
                        }),
                    }}
                />
            )}
            onChange={(event: any, value: any) => {
                setLeaderboardScoreFormat('gross');
                setStandingTabs('individual');
                setEventData(value || {});
                // Update URL to match selection so deep-link effect doesn't override
                const params = new URLSearchParams(searchParams);
                if (value?._id) {
                    params.set('eventID', toIdString(value._id));
                } else {
                    params.delete('eventID');
                    params.delete('eventId');
                }
                setSearchParams(params, { replace: true });
            }}
            sx={[
                defaultSx,
                ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])
            ]}
        />
    );
};

